import React from 'react';
import { PropsWithChildren, createContext, useContext, useState } from "react";
import { CSSState, LayoutState, ThemeState } from "../types/state.types";
import { VERTICAL_TABS } from "../constants/navigation";
import { VerticalTabType } from "../types/navigation.types";

const initialLayoutState: LayoutState = {
    activeComponentId: null,
    components: [],
    containers: [],
    selectedElements: []
};

const initialThemeState: ThemeState = {
    colors: {
        primary: '#1a73e8',
        secondary: '#f1f3f4',
        background: '#ffffff',
        text: '#202124'
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: {
        base: '14px',
        heading: '24px',
        subheading: '18px'
        }
    },
    spacing: {
        unit: '8px',
        container: '16px',
        element: '12px'
    }
};

const initialCSSState: CSSState = {
    customStyles: {},
    globalStyles: ''
};

interface VerticalNavContextType {
    activeTab: VerticalTabType;
    setActiveTab: (tab: VerticalTabType) => void;
    layout: LayoutState;
    setLayout: (layout: LayoutState) => void;
    theme: ThemeState;
    setTheme: (theme: ThemeState) => void;
    css: CSSState;
    setCss: (css: CSSState) => void;
}

const initialState: VerticalNavContextType = {
    activeTab: VERTICAL_TABS.LAYOUT,
    setActiveTab: () => {},
    layout: initialLayoutState,
    setLayout: () => {},
    theme: initialThemeState,
    setTheme: () => {},
    css: initialCSSState,
    setCss: () => {}
};

export const VerticalNavContext = createContext<VerticalNavContextType>(initialState);

export const VerticalNavProvider = ({ children }: PropsWithChildren) => {
    const [activeTab, setActiveTab] = useState<VerticalTabType>(VERTICAL_TABS.LAYOUT);
    const [layout, setLayout] = useState<LayoutState>(initialLayoutState);
    const [theme, setTheme] = useState<ThemeState>(initialThemeState);
    const [css, setCss] = useState<CSSState>(initialCSSState);

    const handleSetActiveTab = (tab: VerticalTabType) => {
        setActiveTab(tab);
    };

    return (
        <VerticalNavContext.Provider 
        value={{
            activeTab,
            setActiveTab: handleSetActiveTab,
            layout,
            setLayout,
            theme,
            setTheme,
            css,
            setCss
        }}
        >
        {children}
        </VerticalNavContext.Provider>
    );
};

export const useVerticalNav = () => {
    const context = useContext(VerticalNavContext);
    if (!context) {
        throw new Error('useVerticalNav must be used within a VerticalNavProvider');
    }
    return context;
};